<script setup lang="ts">
	import { ref } from "vue";

	const dialog = ref<HTMLDialogElement>();

	const loading = ref(false);
	const isOpen = ref(false);
	const data = ref();
	let onClose:
		| ((result: boolean | unknown) => boolean | Promise<boolean>)
		| undefined;

	const open = <T, U>(options: {
		dialogData?: T;
		callback?: (result: U) => boolean | Promise<boolean>;
	}) => {
		dialog.value?.showModal();
		isOpen.value = true;
		data.value = options.dialogData;
		onClose = options.callback as (
			result: boolean | unknown,
		) => boolean | Promise<boolean>;
	};

	async function close<T>(value?: T) {
		if (onClose && value) {
			loading.value = true;
			const result = onClose(value);
			try {
				const success =
					typeof result === "boolean" ? result : await result;
				loading.value = false;
				if (success) {
					dialog.value?.close();
					isOpen.value = false;
				}
			} catch {
				loading.value = false;
			}
		} else {
			dialog.value?.close();
			isOpen.value = false;
			loading.value = false;
		}
	}

	function dialogClosed() {
		loading.value = false;
		isOpen.value = false;
	}

	defineExpose({
		open,
	});
</script>
<template>
	<dialog
		ref="dialog"
		@close="dialogClosed"
	>
		<slot
			v-if="isOpen"
			:close="close"
			:data="data"
			:loading="loading"
		/>
	</dialog>
</template>

<style scoped>
	dialog {
		margin: auto;
		border: none;
		background-color: transparent;
		overflow: visible;
	}
</style>
